import React, { useEffect, useMemo, useState } from 'react';
import { gameplayerAPI, matchplayersAPI } from '../../services/api';
import { parseLineupPasteNumberFirst, buildOwnTeamPastePreview } from '../../utils/playerListParser';
import {
    toStr,
    lineupPlayerKey,
    getLineupPlayerProfile,
    playerDisplayNameFromEntry,
    rosterEntryKey,
    isBetreuerEntry,
} from '../../utils/tickerLineupUtils';

const POSITION_OPTIONS = ['TW', 'LV', 'IV', 'RV', 'LM', 'ZM', 'DM', 'OM', 'RM', 'LA', 'RA', 'ST', 'HS'];

function PositionSelect({ entry, onUpdate }) {
    const stored = entry.positionDetail || (entry.field === 'N/A' ? '' : entry.field) || '';
    const [value, setValue] = useState(stored);

    useEffect(() => {
        setValue(stored);
    }, [entry._id, stored]);

    const save = async (nextValue) => {
        const normalized = nextValue.trim();
        if (normalized === stored) return;
        await onUpdate(entry._id, {
            positionDetail: normalized,
            field: normalized || 'N/A',
        });
    };

    return (
        <select
            className="lineup-pos-select"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                save(e.target.value);
            }}
        >
            <option value="">—</option>
            {POSITION_OPTIONS.map((pos) => (
                <option key={pos} value={pos}>{pos}</option>
            ))}
            {stored && !POSITION_OPTIONS.includes(stored) && (
                <option value={stored}>{stored}</option>
            )}
        </select>
    );
}

function PlayerRow({ entry, availablePlayers, onRemove, onUpdate, bench = false }) {
    const profile = getLineupPlayerProfile(entry);
    const name = playerDisplayNameFromEntry(entry);
    const isStarter = entry.type !== 1;
    const key = lineupPlayerKey(entry);
    const roster = availablePlayers?.find((p) => rosterEntryKey(p) === key || toStr(p.plid?._id || p.plid) === toStr(entry.plid?._id || entry.plid));
    const num = entry.num ?? roster?.num ?? profile.num ?? '';

    return (
        <div className={`lineup-row ${bench ? 'lineup-row--bench' : ''}`}>
            <div className="lineup-jersey">{num || '–'}</div>
            <div className="lineup-name" title={name}>
                {name}
                {entry.isCaptain && <span className="lineup-captain">C</span>}
            </div>
            <PositionSelect entry={entry} onUpdate={onUpdate} />
            <div className="lineup-actions">
                <button
                    type="button"
                    className={`lineup-icon-btn lineup-icon-btn--captain ${entry.isCaptain ? 'lineup-icon-btn--active' : ''}`}
                    onClick={() => onUpdate(entry._id, { isCaptain: !entry.isCaptain })}
                    title="Kapitän"
                >C</button>
                <button
                    type="button"
                    className={`lineup-icon-btn lineup-icon-btn--starter ${isStarter ? 'lineup-icon-btn--active' : ''}`}
                    onClick={() => onUpdate(entry._id, { type: isStarter ? 1 : 0, isStarter: !isStarter })}
                    title={isStarter ? 'Auf dem Feld' : 'Bank'}
                >
                    {isStarter ? '▶' : '⬇'}
                </button>
                <button
                    type="button"
                    className="lineup-icon-btn lineup-icon-btn--danger"
                    onClick={() => onRemove(entry._id)}
                    title="Entfernen"
                >✕</button>
            </div>
        </div>
    );
}

function PlayerGroup({ title, players, availablePlayers, onRemove, onUpdate, bench = false }) {
    if (!players.length) return null;

    return (
        <div className="lineup-group">
            <div className="lineup-group-title">{title}</div>
            <div className="lineup-table-head">
                <span>Nr</span>
                <span>Spieler</span>
                <span>Pos</span>
                <span style={{ textAlign: 'right' }}>Aktion</span>
            </div>
            {players.map((entry) => (
                <PlayerRow
                    key={entry._id}
                    entry={entry}
                    availablePlayers={availablePlayers}
                    onRemove={onRemove}
                    onUpdate={onUpdate}
                    bench={bench}
                />
            ))}
        </div>
    );
}

function rosterLabel(entry) {
    const num = entry.num ? `#${entry.num} ` : '';
    const name = entry.plid?.surname
        ? `${entry.plid.surname}${entry.plid.prename ? `, ${entry.plid.prename}` : ''}`
        : '?';
    return `${num}${name}`.trim();
}

function AddPlayerForm({ teamId, teamName, availablePlayers, existingKeys, onAdd, useMatchPlayers }) {
    const [playerKey, setPlayerKey] = useState('');
    const [search, setSearch] = useState('');
    const [pos, setPos] = useState('');
    const [isStarter, setIsStarter] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    const eligible = availablePlayers
        .filter((entry) => toStr(entry.tid) === toStr(teamId)
            && !existingKeys.includes(rosterEntryKey(entry))
            && !isBetreuerEntry(entry));

    const q = search.trim().toLowerCase();
    const filtered = eligible.filter((entry) => {
        if (!q) return true;
        const hay = `${entry.num || ''} ${entry.plid?.surname || ''} ${entry.plid?.prename || ''}`.toLowerCase();
        return hay.includes(q);
    });

    const pickPlayer = (entry) => {
        setPlayerKey(rosterEntryKey(entry));
        setSearch(rosterLabel(entry));
        setOpen(false);
    };

    const handleAdd = () => {
        if (!playerKey) { setError('Bitte Spieler wählen'); return; }
        setError('');
        const entry = eligible.find((item) => rosterEntryKey(item) === playerKey);
        if (!entry) { setError('Spieler nicht gefunden'); return; }

        const payload = {
            num: entry.num ?? null,
            field: pos || 'N/A',
            positionDetail: pos,
            type: isStarter ? 0 : 1,
            isStarter,
            tid: teamId,
        };

        if (useMatchPlayers || entry.source === 'matchplayer') {
            payload.matchPlayerId = entry.matchPlayerId?._id || entry.matchPlayerId;
        } else {
            payload.plid = entry.plid?._id || entry.plid;
        }

        onAdd(payload);
        setPlayerKey('');
        setSearch('');
        setPos('');
    };

    return (
        <div className="lineup-add">
            <div className="lineup-add-title">Spieler hinzufügen</div>
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
            <div className="lineup-add-grid">
                <div className="lineup-add-search-wrap">
                    <input
                        type="text"
                        className="lineup-add-search"
                        value={search}
                        placeholder={`${teamName} – suchen…`}
                        onChange={(e) => { setSearch(e.target.value); setPlayerKey(''); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 150)}
                    />
                    {open && filtered.length > 0 && (
                        <div className="lineup-add-dropdown">
                            {filtered.slice(0, 25).map((entry) => {
                                const key = rosterEntryKey(entry);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={playerKey === key ? 'is-selected' : ''}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => pickPlayer(entry)}
                                    >
                                        {rosterLabel(entry)}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <select
                    className="lineup-pos-select"
                    value={pos}
                    onChange={(e) => setPos(e.target.value)}
                >
                    <option value="">Pos</option>
                    {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <label className="lineup-add-check">
                    <input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} />
                    Starter
                </label>
                <button type="button" className="lineup-btn lineup-btn--primary" onClick={handleAdd}>
                    + Add
                </button>
            </div>
        </div>
    );
}

function OwnTeamLineupTools({
    teamId,
    teamName,
    gameId,
    teamPlayertools,
    onLineupRefresh,
    onRosterRefresh,
    hasLineup,
}) {
    const [rawText, setRawText] = useState('');
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState('');
    const [importRows, setImportRows] = useState([]);

    const parsed = useMemo(() => parseLineupPasteNumberFirst(rawText), [rawText]);
    const preview = useMemo(
        () => buildOwnTeamPastePreview(parsed, teamPlayertools),
        [parsed, teamPlayertools]
    );

    const handlePasteImport = async () => {
        if (!parsed.length || !teamId) return;
        setBusy('import');
        setMessage('');
        setImportRows([]);
        try {
            const res = await gameplayerAPI.importOwnLineup({
                gid: gameId,
                tid: teamId,
                players: parsed,
                replace: true,
            });

            setImportRows(res.data?.rows || []);
            onRosterRefresh?.(res.data?.playertools || []);
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);

            const summary = res.data?.summary || {};
            const unmatched = summary.unmatched || 0;
            setMessage(
                `${summary.lineupCount || 0} in Aufstellung · `
                + `${summary.rosterAdded || 0} Kader aktualisiert`
                + (unmatched ? ` · ⚠️ ${unmatched} nicht erkannt (manuell hinzufügen)` : '')
            );
            setRawText('');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Import fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    const handleLoadFromRoster = async () => {
        if (!teamId || !teamPlayertools.length) return;
        setBusy('load');
        setMessage('');
        try {
            if (hasLineup && !window.confirm('Bestehende Aufstellung ersetzen?')) {
                setBusy('');
                return;
            }

            await gameplayerAPI.importOwnLineup({
                gid: gameId,
                tid: teamId,
                players: teamPlayertools.map((pt) => ({
                    fullName: `${pt.plid?.prename || ''} ${pt.plid?.surname || ''}`.trim(),
                    prename: pt.plid?.prename || '',
                    surname: pt.plid?.surname || '',
                    num: pt.num,
                    isStarter: true,
                })),
                replace: true,
            });

            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${teamPlayertools.length} Spieler aus Kader übernommen (Feine Pos. bitte setzen).`);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Laden fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    return (
        <details className="lineup-own-tools" open={!hasLineup}>
            <summary>📋 Kader einfügen (FCW / Portal)</summary>
            <div className="lineup-own-tools-body">
                <p className="lineup-own-tools-hint">
                    Liste einfügen – Abgleich nur mit bestehenden Spielern im System (Name).
                    Unbekannte Spieler werden <strong>nicht</strong> angelegt, sondern als Warnung angezeigt.
                    Diese danach manuell hinzufügen (z.&nbsp;B. bei geänderter Trikotnummer).
                </p>
                <textarea
                    className="lineup-own-textarea"
                    placeholder={'01\nNoah Ender\nTor\n0\n0\n0\n0'}
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                />
                {preview.length > 0 && (
                    <div className="lineup-import-preview">
                        <p className="lineup-own-tools-hint">{preview.length} Spieler erkannt</p>
                        <ul className="lineup-import-preview-list">
                            {preview.slice(0, 8).map((row, idx) => (
                                <li key={`${row.fullName}-${idx}`}>
                                    <span className="lineup-import-preview-num">{row.num ?? '–'}</span>
                                    <span>{row.fullName || row.surname}</span>
                                    <span className={`lineup-import-preview-tag lineup-import-preview-tag--${row.status}`}>
                                        {row.message}
                                    </span>
                                </li>
                            ))}
                            {preview.length > 8 && (
                                <li className="lineup-own-tools-hint">… und {preview.length - 8} weitere</li>
                            )}
                        </ul>
                    </div>
                )}
                <div className="lineup-own-actions">
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--primary"
                        disabled={!parsed.length || !!busy}
                        onClick={handlePasteImport}
                    >
                        {busy === 'import' ? 'Import…' : 'Abgleichen & Aufstellung'}
                    </button>
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--ghost"
                        disabled={!!busy || !teamPlayertools.length}
                        onClick={handleLoadFromRoster}
                    >
                        {busy === 'load' ? 'Laden…' : 'Aus Kader übernehmen'}
                    </button>
                </div>
                {importRows.length > 0 && (
                    <ul className="lineup-import-preview-list">
                        {importRows.map((row, idx) => (
                            <li key={`result-${idx}`}>
                                <span>{row.fullName || row.surname}</span>
                                <span className={`lineup-import-preview-tag lineup-import-preview-tag--${row.status}`}>
                                    {row.message}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {message && <p className="lineup-message">{message}</p>}
            </div>
        </details>
    );
}

function OpponentLineupTools({ teamId, teamName, projectId, gameId, onImported, onLineupRefresh, hasLineup }) {
    const [rawText, setRawText] = useState('');
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState('');

    const parsed = useMemo(() => parseLineupPasteNumberFirst(rawText), [rawText]);

    const handlePasteImport = async () => {
        if (!parsed.length || !projectId || !teamId) return;
        setBusy('import');
        setMessage('');
        try {
            const res = await matchplayersAPI.bulkUpsert({
                pid: projectId,
                tid: teamId,
                players: parsed,
            });
            const lineupRes = await matchplayersAPI.applyLineup({
                gid: gameId,
                tid: teamId,
                matchPlayerIds: (res.data?.matchplayers || []).map((mp) => mp._id),
                replace: true,
            });
            onImported?.(res.data?.matchplayers || []);
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${parsed.length} Spieler importiert, ${lineupRes.data?.count || 0} übernommen.`);
            setRawText('');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Import fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    const handleLoadPool = async () => {
        if (!projectId || !teamId) return;
        setBusy('load');
        setMessage('');
        try {
            const res = await matchplayersAPI.applyLineup({ gid: gameId, tid: teamId });
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${res.data?.count || 0} Spieler aus gespeichertem Kader übernommen.`);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Laden fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    return (
        <details className="lineup-opponent-tools" open={!hasLineup}>
            <summary>📋 Gegner-Kader importieren</summary>
            <div className="lineup-opponent-tools-body">
                <p className="lineup-opponent-tools-hint">
                    Liste aus dem Portal einfügen (RNr. zuerst). Positionen danach per Dropdown setzen.
                </p>
                <textarea
                    className="lineup-opponent-textarea"
                    placeholder={'01\nNoah Ender\nTor\n0\n0\n0\n0'}
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                />
                {parsed.length > 0 && (
                    <p className="lineup-opponent-tools-hint">{parsed.length} Spieler erkannt</p>
                )}
                <div className="lineup-opponent-actions">
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--primary"
                        disabled={!parsed.length || !!busy}
                        onClick={handlePasteImport}
                    >
                        {busy === 'import' ? 'Import…' : 'Einfügen & Aufstellung'}
                    </button>
                    <button
                        type="button"
                        className="lineup-btn lineup-btn--ghost"
                        disabled={!!busy}
                        onClick={handleLoadPool}
                    >
                        {busy === 'load' ? 'Laden…' : 'Gespeicherten Kader laden'}
                    </button>
                </div>
                {message && <p className="lineup-message">{message}</p>}
            </div>
        </details>
    );
}

export default function LineupTab({
    game,
    gameId,
    lineup,
    availablePlayers,
    onLineupChange,
    trackedTeamId,
    onMatchPlayersChange,
    onRosterRefresh,
}) {
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState('');

    const homeId = toStr(game?.hid?._id || game?.hid);
    const awayId = toStr(game?.aid?._id || game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';
    const projectId = toStr(game?.pid?._id || game?.pid);
    const trackedId = toStr(trackedTeamId);

    const forTeam = (teamId) => lineup.filter((e) => toStr(e.tid) === toStr(teamId));
    const existingKeys = (teamId) => forTeam(teamId).map((e) => lineupPlayerKey(e));

    const handleAdd = async (teamId, data) => {
        setError('');
        setSaving('add');
        try {
            const payload = {
                gid: gameId,
                pid: projectId,
                tid: teamId,
                num: data.num ?? null,
                field: data.field || 'N/A',
                positionDetail: data.positionDetail || '',
                type: data.type,
                isStarter: data.isStarter,
                isCaptain: data.isCaptain || false,
                minute: 0,
            };

            if (data.matchPlayerId) payload.matchPlayerId = data.matchPlayerId;
            else payload.plid = data.plid;

            const res = await gameplayerAPI.create(payload);
            onLineupChange((prev) => [...prev, res.data]);
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Hinzufügen');
        } finally {
            setSaving(null);
        }
    };

    const handleUpdate = async (id, updates) => {
        setSaving(id);
        onLineupChange((prev) => prev.map((e) => (e._id === id ? { ...e, ...updates } : e)));
        try {
            const res = await gameplayerAPI.update(id, updates);
            const updated = res.data?.data || res.data;
            if (updated?._id) {
                onLineupChange((prev) => prev.map((e) => (e._id === id ? { ...e, ...updated } : e)));
            }
        } catch (err) {
            setError('Fehler beim Aktualisieren');
            const lineupRes = await gameplayerAPI.getByGame(gameId);
            onLineupChange(lineupRes.data || []);
        } finally {
            setSaving(null);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm('Spieler aus Aufstellung entfernen?')) return;
        setSaving(id);
        try {
            await gameplayerAPI.delete(id);
            onLineupChange((prev) => prev.filter((e) => e._id !== id));
        } catch (err) {
            setError('Fehler beim Entfernen');
        } finally {
            setSaving(null);
        }
    };

    const TeamSection = ({ teamId, teamName, isOpponent }) => {
        const players = forTeam(teamId);
        const starters = players.filter((p) => p.type !== 1);
        const subs = players.filter((p) => p.type === 1);
        const teamPlayertools = availablePlayers.filter(
            (entry) => toStr(entry.tid) === toStr(teamId)
                && entry.source !== 'matchplayer'
                && !isBetreuerEntry(entry)
        );

        return (
            <section className="lineup-team-card">
                <header className="lineup-team-head">
                    <div className="lineup-team-title">
                        <span>{teamName}</span>
                        <span className={`lineup-team-badge ${isOpponent ? 'lineup-team-badge--opp' : 'lineup-team-badge--own'}`}>
                            {isOpponent ? 'Gegner' : 'Eigenes Team'}
                        </span>
                    </div>
                    <div className="lineup-team-stats">
                        <span className="lineup-stat-pill lineup-stat-pill--field">{starters.length} Feld</span>
                        <span className="lineup-stat-pill">{subs.length} Bank</span>
                    </div>
                </header>

                {!isOpponent && (
                    <OwnTeamLineupTools
                        teamId={teamId}
                        teamName={teamName}
                        gameId={gameId}
                        teamPlayertools={teamPlayertools}
                        hasLineup={players.length > 0}
                        onLineupRefresh={onLineupChange}
                        onRosterRefresh={(playertools) => onRosterRefresh?.(teamId, playertools)}
                    />
                )}

                {isOpponent && (
                    <OpponentLineupTools
                        teamId={teamId}
                        teamName={teamName}
                        projectId={projectId}
                        gameId={gameId}
                        hasLineup={players.length > 0}
                        onImported={(matchPlayers) => onMatchPlayersChange?.(teamId, matchPlayers)}
                        onLineupRefresh={onLineupChange}
                    />
                )}

                <div className="lineup-body">
                    {!players.length && (
                        <div className="lineup-empty">
                            {isOpponent
                                ? 'Noch keine Aufstellung – Kader importieren oder Spieler hinzufügen.'
                                : 'Noch keine Aufstellung – Kader einfügen oder aus Kader übernehmen.'}
                        </div>
                    )}

                    <PlayerGroup
                        title="Startelf"
                        players={starters}
                        availablePlayers={availablePlayers}
                        onRemove={handleRemove}
                        onUpdate={handleUpdate}
                    />
                    <PlayerGroup
                        title="Ersatzbank"
                        players={subs}
                        availablePlayers={availablePlayers}
                        onRemove={handleRemove}
                        onUpdate={handleUpdate}
                        bench
                    />

                    <AddPlayerForm
                        teamId={teamId}
                        teamName={teamName}
                        availablePlayers={availablePlayers}
                        existingKeys={existingKeys(teamId)}
                        onAdd={(data) => handleAdd(teamId, data)}
                        useMatchPlayers={isOpponent}
                    />
                </div>
            </section>
        );
    };

    return (
        <div className="lineup-root">
            {error && (
                <div className="lineup-alert">
                    <span>⚠️ {error}</span>
                    <button type="button" className="lineup-icon-btn lineup-icon-btn--danger" style={{ marginLeft: 'auto' }} onClick={() => setError('')}>✕</button>
                </div>
            )}
            {saving === 'add' && <div className="lineup-status">Speichern…</div>}

            <div className="lineup-grid">
                <TeamSection teamId={homeId} teamName={homeName} isOpponent={trackedId ? homeId !== trackedId : false} />
                <TeamSection teamId={awayId} teamName={awayName} isOpponent={trackedId ? awayId !== trackedId : true} />
            </div>
        </div>
    );
}
